import type React from "react";

import type { PollResultDto, UserDto } from "@/api/types";
import { Button } from "@/components/Button/Button";
import { Star01Icon } from "@/components/Icons/Star01Icon";
import { Users01Icon } from "@/components/Icons/Users01Icon";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { UserAvatarLink } from "@/components/UserAvatarLink/UserAvatarLink";
import { UserNameLink } from "@/components/UserNameLink/UserNameLink";
import { useTranslation } from "@/translations";

type CommunityPollResultModalProps = ModalBaseProps & {
  question: string;
  results: PollResultDto[];
};

export function CommunityPollResultModal({
  question,
  results,
  isOpened,
  onOpenChange,
}: CommunityPollResultModalProps): React.ReactNode {
  const { t } = useTranslation();

  const allVoters = results.reduce<UserDto[]>((acc, result) => acc.concat(result.voters), []);
  const amountUniqueVoters = new Set(allVoters.map((voter) => voter.id)).size;

  return (
    <Modal.Root
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      title={t("component.community-poll.result-modal.title")}
    >
      <div className="flex flex-col gap-4">
        <h4 className="text-headline4">{question}</h4>

        <div className="flex items-center gap-1 text-grey-700">
          <Users01Icon size={24} />
          <span className="text-caption text-grey-700">
            {t("component.community-poll.result-modal.label.amount-people-voted", {
              count: amountUniqueVoters,
            })}
          </span>
        </div>

        <ul className="flex flex-col gap-4">
          {results.map((result) => (
            <li key={result.answer.id} className="flex flex-col rounded-lg border border-grey-300">
              <div className="flex items-center justify-between p-4">
                <span className="text-body">{result.answer.text}</span>
                <div className="flex items-center gap-2">
                  {result.hasWon && <Star01Icon className="fill-yellow-500 text-yellow-500" size={20} />}
                  <span className="text-body-bold">{result.count}</span>
                </div>
              </div>

              {result.voters.length > 0 && (
                // Limit height of container to reveal 3 and half voters at a time
                <ul className="flex max-h-[176px] flex-col gap-4 overflow-y-auto border-t border-grey-100 p-4">
                  {result.voters.map((voter) => (
                    <li key={voter.id} className="flex items-center gap-2">
                      <div className="size-8">
                        <UserAvatarLink user={voter} />
                      </div>
                      <UserNameLink user={voter}>
                        <span className="text-body-bold">{voter.fullName}</span>
                      </UserNameLink>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
      <Modal.Actions>
        <Modal.Close>
          <Button>{t("common.action.close")}</Button>
        </Modal.Close>
      </Modal.Actions>
    </Modal.Root>
  );
}
